import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentsService, IOcbCULocalAgentMaintenance, IOcbLocalAgent, IOcbLocalAgentMaintenance, LocalAgentsService, UserService } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxSelectBoxComponent } from 'devextreme-angular';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxValidationGroupComponent } from 'devextreme-angular/ui/validation-group';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { Observable, forkJoin, from as fromPromise } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';

@Component({
    selector: 'app-local-agents-maintenances',
    templateUrl: './local-agents-maintenances.component.html',
    styleUrls: ['./local-agents-maintenances.component.scss'],
})
export class LocalAgentsMaintenancesComponent implements AfterViewInit {

    @ViewChild('maintenances')
    public mGrid: DxDataGridComponent;
    @ViewChild('maintenanceValidation')
    public mValidationGroup: DxValidationGroupComponent;
    @ViewChild('agentPicker')
    public agentPicker: DxSelectBoxComponent;

    // Main Data-Grid
    public dataSource: IOcbLocalAgentMaintenance[] = null;

    // List of local agents
    public localAgents: IOcbLocalAgent[] = null;
    public requestedAgentId?: string;
    public selectedLocalAgent: IOcbLocalAgent = null;

    // Popup New & Edit
    public isPopupVisible: boolean = false;
    public selectedMaintenance: IOcbLocalAgentMaintenance = {
        localAgentId: '',
        description: '',
    };
    public isNew: boolean = null;

    public isDropDownBoxOpened: boolean = false;
    public isLoadPanelVisible: boolean = true;
    public currentEnvironmentName: string = '';

    private currentEnvironmentId: string = '';

    constructor(
        private route: ActivatedRoute,
        private userService: UserService,
        private translateService: TranslateService,
        private environmentsService: EnvironmentsService,
        private localAgentsService: LocalAgentsService) {
        this.displayDateStartOn = this.displayDateStartOn.bind(this);
        this.displayDateEndOn = this.displayDateEndOn.bind(this);
        this.displayDate = this.displayDate.bind(this);
    }

    public ngAfterViewInit() {
        this.route.queryParams.subscribe(queryParams => {
            if (queryParams['agent']) this.requestedAgentId = queryParams['agent'];
        });
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.environmentsService.getEnvironment(this.currentEnvironmentId).subscribe(env => this.currentEnvironmentName = env.name);
            this.loadLocalAgents();
        });
    }

    public displayNewPopup(): void {
        if (this.selectedLocalAgent) {
            this.selectedMaintenance.localAgentId = null;
            this.selectedMaintenance.description = null;
            this.isNew = true;
            this.isPopupVisible = true;
        }
        else 
            this.translateService.get('localAgentsMaintenances.msgLocalAgentRequired').subscribe(message => notify(message, 'error', 3000));
    }

    public displayEditPopup(maintenance: IOcbLocalAgentMaintenance): void {
        this.selectedMaintenance = {
            localAgentId: maintenance.localAgentId,
            id: maintenance.id,
            description: maintenance.description,
            startOn: maintenance.startOn,
            endOn: maintenance.endOn,
        };
        this.isNew = false;
        this.isPopupVisible = true;
    }

    public createMaintenance(): void {
        if (this.mValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const maintenance: IOcbCULocalAgentMaintenance = {
                description: this.selectedMaintenance.description,
                startOn: this.selectedMaintenance.startOn,
                endOn: this.selectedMaintenance.endOn,
            };
            this.localAgentsService.addMaintenance(this.selectedLocalAgent.id, maintenance)
                .pipe(
                    mergeMap(_ => this.translateService.get('localAgentsMaintenances.msgCreationComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe({
                    next: (message) => {
                        this.isPopupVisible = false;
                        this.loadDataSource();
                        notify(message, 'success', 3000);
                    },
                    error: (err) => this.handleError(err)
                });
        }
    }

    public updateMaintenance(): void {
        if (this.mValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const maintenance: IOcbCULocalAgentMaintenance = {
                description: this.selectedMaintenance.description,
                startOn: this.selectedMaintenance.startOn,
                endOn: this.selectedMaintenance.endOn,
            };
            this.localAgentsService.updateMaintenance(this.selectedLocalAgent.id, this.selectedMaintenance.id, maintenance)
                .pipe(
                    mergeMap(_ => this.translateService.get('localAgentsMaintenances.msgUpdateComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe({
                    next: (message) => {
                        this.isPopupVisible = false;
                        this.loadDataSource();
                        notify(message, 'success', 3000);
                    },
                    error: (err) => this.handleError(err)
                });
        }
    }

    public deleteMaintenance(id): void {
        forkJoin([
            this.translateService.get('localAgentsMaintenances.msgDeleteConfirm'),
            this.translateService.get('localAgentsMaintenances.msgDeleteConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.localAgentsService.deleteMaintenance(this.selectedLocalAgent.id, id)),
                // Reload the email template
                mergeMap(_ => this.translateService.get('localAgentsMaintenances.msgDeleteComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    public displayDateStartOn(rowData): string { return this.displayDate(rowData.startOn); }

    public displayDateEndOn(rowData): string { return this.displayDate(rowData.endOn); }

    public displayDate(date: string): string {
        let currentLocale = 'en-US';
        let dte = new Date(date);
        switch (this.userService.language) {
            case 'en': currentLocale = 'en-US'; break;
            case 'fr': currentLocale = 'fr-FR'; dte = new Date(date + 'Z'); break;
        }
        return date ? dte.toLocaleString(currentLocale) : '';
    }

    public filterChanged(e): void {
        if (e) {
            this.selectedLocalAgent = e;
            this.isDropDownBoxOpened = false;
            this.loadDataSource();
        }
        else
            this.dataSource = [];
    }

    private loadLocalAgents(): void {
        this.localAgentsService.getLocalAgents(this.currentEnvironmentId, false)
            .pipe(
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (res) => {
                    this.localAgents = res;
                    if (this.requestedAgentId && this.agentPicker) this.agentPicker.value = this.requestedAgentId;
                },
                error: (err) => this.handleError(err)
            });
    }

    private loadDataSource(): void {
        if (this.selectedLocalAgent) {
            this.showLoading$('_commons.loading')
            .pipe(
                mergeMap(_ => this.localAgentsService.getMaintenances(this.selectedLocalAgent.id)),
                finalize(() => this.mGrid.instance.endCustomLoading()))
                .subscribe({
                    next: (res) => this.dataSource = res,
                    error: (err) => this.handleError(err)
                });
        }
        else 
            this.translateService.get('_pickers.msgLocalAgentRequired').subscribe(message => notify(message, 'error', 3000));
    }

    private showLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.mGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    private handleError(error: any): void {
        let message: string = '';
        if (error instanceof HttpErrorResponse) {
            if (error.error) {
                if (error.error.hasOwnProperty('message')) message = error.error.message;
                else if (error.error.hasOwnProperty('name')) message = error.error.name;
                else message = error.error;
            }
            else 
                message = error.message;
        }
        else
            message = error;
        notify(message, 'error', 3000);
    }
}
